import { FieldProps } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileUploadInputStatus,
  FileUploadProperty,
  FileUploadThumbnail,
  FILE_UPLOAD_INPUT_STATUS,
  FormPageInputData,
  FormPageInputItemFileUploadPiclib,
} from 'shared';
import { useFileUpload } from 'store';
import { InputItemAccessLogCollector } from '../../../../AccessLogCollector';
import { formatDateWithTimezoneOffset } from '../../../../AccessLogCollector/getFormatDate';
import { hasErrorMessage } from '../../shared';
import { useFieldPropsHandler } from '../../useFieldPropsHandler';
import { isFileUploadStateReady } from '../models';
import {
  FILE_UPLOAD_RESULT_TYPE,
  useFileUploadEventHandler,
} from '../useFileUploadEventHandler';
import { useUploadedFileHandler } from '../useUploadedFileHandler';

const sleep = (timeout: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, timeout);
  });

type Payload = {
  property: FileUploadProperty;
  fieldProps: FieldProps<FormPageInputItemFileUploadPiclib, FormPageInputData>;
  submitted: boolean;
  required: boolean;
};

type ReturnType = {
  onSelect: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
  onReload: () => Promise<void>;
  onChange: (file: File) => Promise<void>;
  displayError: boolean;
  sessionError: boolean;
  reloading: boolean;
  errorMessages: string[];
  status: FileUploadInputStatus;
  thumbnail: FileUploadThumbnail | undefined;
};

export const useFileUploadHandler = ({
  property,
  fieldProps,
  submitted,
  required,
}: Payload): ReturnType => {
  const [status, setStatus] = useState<FileUploadInputStatus>(
    FILE_UPLOAD_INPUT_STATUS.notReady,
  );
  const [itemErrorMessages, setItemErrorMessages] = useState<string[]>([]);
  const fileUpload = useFileUpload();
  const { propertyId } = property;
  const { subjectId } = property.inputFormat;
  const {
    fieldTouched,
    fieldErrorMessages,
    updateFieldValue,
    updateFieldTouched,
  } = useFieldPropsHandler<FormPageInputItemFileUploadPiclib>({
    fieldProps,
    propertyId,
  });

  const handleUpdateNextState = useCallback(
    (nextStatus: Extract<FileUploadInputStatus, 'ready' | 'hasFile'>) => {
      setStatus(nextStatus);
    },
    [],
  );

  const handleFileNotFound = useCallback(() => {
    setStatus(FILE_UPLOAD_INPUT_STATUS.ready);
    updateFieldValue('fileSrn', '');
  }, [updateFieldValue]);

  const handleNeedsToReload = useCallback(() => {
    setStatus(FILE_UPLOAD_INPUT_STATUS.needsToReload);
    setItemErrorMessages([
      fileUpload.setting?.errorMessages.GET_UPLOADED_FILE_INFO_ERROR ?? '',
    ]);
  }, [fileUpload.setting?.errorMessages.GET_UPLOADED_FILE_INFO_ERROR]);

  // ファイルアップロードの入力項目アクセスログを記録
  const recordFileUploadAccessLog = useCallback(
    (startTime: Date, errorTypes: string[]) => {
      const inputDuration = new Date().getTime() - startTime.getTime();
      const log = {
        itemId: propertyId,
        accessTimestamp: formatDateWithTimezoneOffset(startTime),
        errorTypes,
        inputDuration,
      };
      InputItemAccessLogCollector.recordInputAccessLog(log);
    },
    [propertyId],
  );

  const { sessionError, prepare, reload, reloading } = useUploadedFileHandler({
    propertyId,
    subjectId,
    onUpdateNextState: handleUpdateNextState,
    onFileNotFound: handleFileNotFound,
    onNeedsToReload: handleNeedsToReload,
  });

  const errorMessages = useMemo(
    () => [...itemErrorMessages, ...(fieldErrorMessages ?? [])],
    [fieldErrorMessages, itemErrorMessages],
  );

  const { uploadAndVerifyFile, deleteFromTemporaryFile } =
    useFileUploadEventHandler(property);

  const handleSelect = useCallback(
    async (file: File) => {
      const startTime = new Date();
      const validStatus =
        status === FILE_UPLOAD_INPUT_STATUS.ready ||
        status === FILE_UPLOAD_INPUT_STATUS.hasFile;
      const beforeStatus = status;
      if (!isFileUploadStateReady(fileUpload) || !validStatus) {
        return;
      }
      setItemErrorMessages([]);
      setStatus(FILE_UPLOAD_INPUT_STATUS.uploading);
      const { session, setting } = fileUpload;

      const result = await uploadAndVerifyFile({
        file,
        session,
        setting,
      });
      if (result.type === FILE_UPLOAD_RESULT_TYPE.success) {
        updateFieldValue('fileSrn', result.fileSrn);
        setStatus(FILE_UPLOAD_INPUT_STATUS.uploadComplete);
        recordFileUploadAccessLog(startTime, []);
        await sleep(1000);
        setStatus(FILE_UPLOAD_INPUT_STATUS.hasFile);
      } else {
        setStatus(beforeStatus);
        setItemErrorMessages(result.errorMessages);
        recordFileUploadAccessLog(startTime, result.errorTypes);
      }
      updateFieldTouched('fileSrn');
    },
    [
      fileUpload,
      status,
      uploadAndVerifyFile,
      updateFieldTouched,
      updateFieldValue,
      recordFileUploadAccessLog,
    ],
  );

  const handleDelete = useCallback(async () => {
    const startTime = new Date();
    if (
      !isFileUploadStateReady(fileUpload) ||
      status !== FILE_UPLOAD_INPUT_STATUS.hasFile
    ) {
      return;
    }
    setItemErrorMessages([]);
    setStatus(FILE_UPLOAD_INPUT_STATUS.deleting);
    const { session, setting } = fileUpload;

    const result = await deleteFromTemporaryFile({
      session,
      setting,
    });
    if (result.type === FILE_UPLOAD_RESULT_TYPE.success) {
      updateFieldValue('fileSrn', result.fileSrn);
      setStatus(FILE_UPLOAD_INPUT_STATUS.ready);
      // 必須のファイルアップロード項目のファイル削除時は required を記録
      const errorTypes = required ? ['required'] : [];
      recordFileUploadAccessLog(startTime, errorTypes);
    } else {
      setStatus(FILE_UPLOAD_INPUT_STATUS.hasFile);
      setItemErrorMessages(result.errorMessages);
      // 削除エラーを記録
      recordFileUploadAccessLog(startTime, result.errorTypes);
    }
    updateFieldTouched('fileSrn');
  }, [
    fileUpload,
    status,
    required,
    deleteFromTemporaryFile,
    updateFieldTouched,
    updateFieldValue,
    recordFileUploadAccessLog,
  ]);

  const handleReload = useCallback(async () => {
    const fileSrn = fieldProps.field.value?.fileSrn;
    if (!fileSrn) {
      setStatus(FILE_UPLOAD_INPUT_STATUS.ready);
      return;
    }
    await reload({ fileSrn });
  }, [fieldProps.field.value?.fileSrn, reload]);

  const initialize = useCallback(async () => {
    if (status !== FILE_UPLOAD_INPUT_STATUS.notReady) {
      return;
    }
    // initialize は `FileUploadPiclib` 生成時にコールされるが、fieldProps 初期化前に initialize が実行されてしまうと、以下の不具合が発生してしまう.
    // 不具合1: https://gitlab.dev-internal.crmstyle.com/synergy/newt/form/portal/-/issues/3957
    // 不具合2: https://gitlab.dev-internal.crmstyle.com/synergy/newt/form/portal/-/issues/3958
    // fieldProps が不完全な場合は、initialize 実行されないようにここで return している
    if (fieldProps.field.value == null) {
      return;
    }
    const fileSrn = fieldProps.field.value.fileSrn;
    await prepare({
      fileSrn,
    });
  }, [fieldProps.field.value, prepare, status]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const hasError =
    (fieldTouched || submitted) && hasErrorMessage(errorMessages);
  const displayError =
    status === FILE_UPLOAD_INPUT_STATUS.needsToReload || hasError;

  return {
    onSelect: handleSelect,
    onDelete: handleDelete,
    onChange: handleSelect,
    onReload: handleReload,
    displayError,
    sessionError,
    reloading,
    errorMessages,
    status,
    thumbnail: fileUpload.items[subjectId]?.thumbnail,
  };
};
